<template>
  <message-dialog
    v-if="Boolean(usedType)"
    :dialog="true"
    :fullscreen="$vuetify.display.mobile"
    disable-content-padding
    :ok-button-loading="updating"
    transition="dialog-bottom-transition"
    persistent
    @cancel="onCancel"
    @ok="agreeToDocument"
  >
    <template #header v-if="$vuetify.display.mobile">
      <header-mobile post-background>
        <template v-if="!acceptanceMode" #item-left>
          <the-button icon small @click="close">
            <i-custom-close class="close-icon" />
          </the-button>
        </template>
        <template #item-middle>
          <span />
        </template>
        <template #item-right>
          <div v-if="acceptanceMode" class="d-flex justify-end">
            <the-button v-if="!updating" text class="ml-0 mr-n2 color-post-tint" custom-colors @click="logOut">
              {{ $translate("common.logOut") }}
            </the-button>
            <the-button v-if="iframeInitialized" text class="ml-0 mr-n2 color-post-tint" custom-colors :loading="updating" @click="agreeToDocument">
              {{ $translate("LegalDocuments.agree") }}
            </the-button>
          </div>
          <span v-else />
        </template>
      </header-mobile>
    </template>
    <template #cancel v-if="!$vuetify.display.mobile && !updating">
      <template v-if="acceptanceMode">
        {{ $translate("common.logOut") }}
      </template>
      <template v-else>{{ $translate("common.close") }}</template>
    </template>
    <template #ok v-if="!$vuetify.display.mobile && acceptanceMode && iframeInitialized">
      <div class="agree-button">{{ $translate("LegalDocuments.agree") }}</div>
    </template>
    <template #content>
      <div v-if="loadingDocument" class="d-flex justify-center">
        <loading-indicator class="my-12 color-post-tint" />
      </div>
      <iframe v-if="src" v-show="!loadingDocument" ref="iframe" class="iframe fill-width border-none" :src="src" @load="onIframeLoaded" />
    </template>
  </message-dialog>
</template>

<script>
import { mapGetters, mapActions } from "pinia"

import { useUserSessionStore } from "@/store/userSession.js"
import { useConfigurationStore } from "@/store/configuration.js"

import * as accountsApi from "@/api/accountsApi.js"

const docTypes = {
  privacyPolicy: { url: "/privacy_policy" },
  termsConditions: { url: "/terms_and_conditions" },
  support: { url: "/support" }
}

import { MOBILE_TOP_BAR_HEIGHT } from "@/constants/barHeights.js"

export default {
  props: {
    type: {
      type: String,
      default: null,
      validator: (value) => docTypes[value]
    },
    acceptanceMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      updating: false,
      iframeInitialized: false,
      usedType: this.type,
      loadingDocument: true
    }
  },
  computed: {
    ...mapGetters(useConfigurationStore, ["configuration"]),
    ...mapGetters(useUserSessionStore, ["privacyPolicyOutdated", "termsAndConditionsOutdated"]),
    legalDocOutdated() {
      return this.privacyPolicyOutdated || this.termsAndConditionsOutdated
    },
    typeData() {
      return docTypes[this.usedType]
    },
    src() {
      if (!this.typeData) {
        return null
      }
      return this.typeData.url
    }
  },
  watch: {
    legalDocOutdated: {
      immediate: true,
      handler() {
        this.validateLegalDocuments()
      }
    },
    type(value) {
      if (!this.acceptanceMode) {
        this.usedType = value
      }
    },
    usedType() {
      this.iframeInitialized = false
      this.loadingDocument = true
    }
  },
  methods: {
    ...mapActions(useUserSessionStore, ["setUserData"]),
    async onIframeLoaded() {
      this.iframeInitialized = true
      await this.$nextTick()
      const iframe = this.$refs.iframe
      this.loadingDocument = false
      await this.$nextTick()

      if (this.$vuetify.display.mobile) {
        iframe.style.height = `${window.innerHeight - MOBILE_TOP_BAR_HEIGHT}px`
        return
      }

      const body = iframe.contentWindow.document.body
      const minimalHeight = 100

      const contentHeight = Math.min(window.innerHeight - 200, body.scrollHeight + minimalHeight)
      iframe.style.height = `${contentHeight}px`
    },
    async validateLegalDocuments() {
      if (!this.acceptanceMode) {
        return
      }
      this.usedType = null
      await this.$nextTick()
      if (this.privacyPolicyOutdated) {
        this.usedType = "privacyPolicy"
      } else if (this.termsAndConditionsOutdated) {
        this.usedType = "termsConditions"
      }
    },
    onCancel() {
      if (this.acceptanceMode) {
        this.logOut()
      } else {
        this.close()
      }
    },
    close() {
      this.validateLegalDocuments()
      this.$emit("close")
    },
    logOut() {
      this.usedType = null
      this.$emit("log-out")
    },
    async agreeToDocument() {
      try {
        this.updating = true

        const data = {}
        if (this.usedType === "privacyPolicy") {
          data.accepted_privacy_policy_version = this.configuration.privacyPolicyVersion
        } else {
          data.accepted_terms_and_conditions_version = this.configuration.termsAndConditionsVersion
        }
        await accountsApi.agreeToDocument(data)
        await this.setUserData({ throwable: true })
      } catch (err) {
        this.$reportError(err, this.$translate("common.somethingWrong"))
      } finally {
        this.updating = false
        this.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
iframe {
  background-color: white;
}
</style>
